import { SearchUnifiedShipmentsBookingResponse } from '@forto-dev/search-light-models'

import { FCLCargoDTO } from '@/clients/booking.ts'
import { getBookingTimeline, getMainCarriageTransportMode } from '@/features/bookings'

import { OverviewItem, OverviewItemSchema } from '../domain/overviewItem.ts'
import { ResolvedAddressMap, ResolvedLegalEntityMap } from '../types'

const getCargo = (cargoFCL: FCLCargoDTO | null) => {
  if (!cargoFCL) {
    return []
  }

  return cargoFCL.containers.map((container) => {
    return {
      ...container,
      grossWeightKg: cargoFCL.grossWeight,
      commodity: cargoFCL.commodity,
    }
  })
}

export const unifiedBookingToOverviewItem = (
  booking: SearchUnifiedShipmentsBookingResponse,
  resolvedAddresses?: ResolvedAddressMap,
  resolvedLegalEntities?: ResolvedLegalEntityMap,
  hasCustoms = false
): OverviewItem => {
  const transportMode = getMainCarriageTransportMode(booking.waypoints || [])

  const result = OverviewItemSchema.safeParse({
    id: booking.id,
    type: 'booking',
    transportMode: transportMode as 'sea' | 'air' | 'seaAir' | 'rail' | 'road',
    transportType: booking.transportType,
    containerQuantity: booking.cargoFCL?.containers?.length || 0,
    state: booking.state as
      | 'rejected'
      | 'new'
      | 'active'
      | 'completed'
      | 'canceled'
      | 'carrierContacted'
      | 'nominated'
      | 'authorized'
      | 'open'
      | 'confirmed'
      | 'closed'
      | 'shipmentCreated',
    bookedDate: booking.bookedAt as unknown as Date,
    createdDate: booking.bookedAt as unknown as Date,
    pickupDate: booking.bookedAt as unknown as Date,
    deliveryDate: booking.deliveryDueAt as unknown as Date,
    customerReference: booking.customerReference || '',
    // Comes from searchlight: the booking in this response carries nothing to derive it from
    hasCustoms,
    shipper:
      booking.shipper?.companyName ||
      (booking.shipperLegalEntityID && resolvedLegalEntities?.get(booking.shipperLegalEntityID)?.name) ||
      '',
    consignee:
      booking.consignee?.companyName ||
      (booking.consigneeLegalEntityID && resolvedLegalEntities?.get(booking.consigneeLegalEntityID)?.name) ||
      '',
    cargo: getCargo(booking.cargoFCL),
    containerPlans: [],
    timeline: getBookingTimeline({
      waypoints: booking.waypoints || [],
      cargoReadyAt: booking.cargoReadyAt || '',
      departureAt: booking.departureAt || '',
      arrivalAt: booking.arrivalAt || '',
      deliveryDueAt: booking.deliveryDueAt || '',
      originPortName: booking.portOfLoading.name || '',
      destinationPortName: booking.portOfDischarge.name || '',
      destinationCountryCode: booking.portOfDischarge.countryCode ?? undefined,
      resolvedAddresses,
    }),
  })
  if (!result.success) {
    // eslint-disable-next-line no-console
    console.error('Zod Validation Error:', result.error.format())
    throw new Error('Invalid data structure')
  }

  return result.data
}
